import { Component } from '@theme/component';
import {
  getScrollContainer,
  getScrollTop,
  getScrollEventTarget,
  scrollTo,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * Aufbau-Spur „Drei Teile. Ein Filter." (`sections/parts-rail.liquid`).
 *
 * Portiert aus dem Entwurf (`design/03_Shop/shop-hell/index.html`, Skript nach
 * `.vxc`). Verhalten 1:1, mit drei Anpassungen an Horizon:
 *
 * 1. **Scrollport.** Ab 990 px scrollt `.page-wrapper`, nicht das Fenster —
 *    `window.scrollY`/`scrollBy` und ein `scroll`-Zuhörer am Fenster liefen dort
 *    ins Leere. Alles über `@theme/scroll-container`, und der Zuhörer zieht
 *    beim Wechsel über die 990-px-Grenze um (erst abhängen, dann neu binden).
 * 2. **Section-Reload im Editor.** Zuhörer hängen am Custom Element selbst
 *    (Delegation), nicht an Kindern, die ein Morph ersetzen kann. Das
 *    Scrollen der Spur fängt ein Capture-Zuhörer am Element — `scroll` bubbelt
 *    nicht, wird aber in der Capture-Phase durchgereicht.
 * 3. **Fokus in der fahrenden Spur.** Am Desktop liegt die Spur in einer
 *    Bühne mit `overflow: hidden`. Tabbt man auf ein Element außerhalb des
 *    sichtbaren Ausschnitts, scrollt der Browser die Bühne selbst — die Spur
 *    stünde dann doppelt verschoben. Stattdessen wird die Seite so weit
 *    gescrollt, dass die fokussierte Kachel im Bild ist.
 * 4. **Bühnenhöhe (Abweichung vom Entwurf, Maik 2026-10-09).** Im Entwurf ist
 *    die Bühne bildschirmhoch und der Inhalt steht darin mittig — beim Ein-
 *    und Ausfahren liegt oben und unten je die halbe Restluft. Gewünscht ist
 *    etwa die Hälfte davon. Die Bühne ist deshalb nur noch Inhalt plus ein
 *    Drittel der Restluft hoch (`AIR`) und klebt mittig im Bildschirm (`top` =
 *    Versatz). Während der Fahrt steht der Inhalt damit exakt wie vorher; nur
 *    der Abstand zu den Nachbar-Modulen schrumpft. Alle Stellen, die die Fahrt aus der
 *    Lage der Spur lesen, gehen über `#travel()`.
 *
 * @typedef {object} PartsRailRefs
 * @property {HTMLElement} track
 * @property {HTMLElement} stage
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement} arrow
 *
 * @extends {Component<PartsRailRefs>}
 */
export class PartsRail extends Component {
  requiredRefs = ['track', 'stage', 'rail', 'arrow'];

  /** Auslauf nach der Fahrt, als Anteil der Viewporthöhe (Entwurf: 30 %). */
  static TAIL = 0.3;

  /**
   * Anteil der Restluft (Bildschirm minus Inhalt), den die Bühne behält.
   * Entwurf: 1. Ein Drittel halbiert etwa den sichtbaren Abstand zu den
   * Nachbar-Modulen, gemessen bei 1440×900 und 1920×1080.
   */
  static AIR = 1 / 3;

  /** Vergrößerung der Lupe (Entwurf: `Z = 3`). */
  static ZOOM = 3;

  static #desktop = matchMedia('(min-width: 900px)');
  static #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {HTMLElement[]} */
  #cards = [];
  #weg = 0;
  /** Klebeversatz der Bühne von oben, s. Punkt 4. */
  #top = 0;
  #atEnd = false;
  #ticking = false;
  #frame = 0;
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  #connected = false;

  connectedCallback() {
    super.connectedCallback();
    this.#connected = true;

    this.#cards = this.#queryCards();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('scroll', this.#onInnerScroll, { capture: true, passive: true });
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerout', this.#onPointerOut);
    this.addEventListener('focusin', this.#onFocusIn);
    document.addEventListener('click', this.#onDocumentClick);

    this.#bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#rebind);
    PartsRail.#desktop.addEventListener('change', this.#measure);
    PartsRail.#reducedMotion.addEventListener('change', this.#measure);
    addEventListener('resize', this.#measure, { passive: true });

    if (document.readyState !== 'complete') addEventListener('load', this.#measure, { once: true });
    document.fonts?.ready.then(() => this.#connected && this.#measure());

    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connected = false;

    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('scroll', this.#onInnerScroll, { capture: true });
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerout', this.#onPointerOut);
    this.removeEventListener('focusin', this.#onFocusIn);
    document.removeEventListener('click', this.#onDocumentClick);

    this.#scrollTarget?.removeEventListener('scroll', this.#requestUpdate);
    this.#scrollTarget = null;
    scrollContainerMediaQuery.removeEventListener('change', this.#rebind);
    PartsRail.#desktop.removeEventListener('change', this.#measure);
    PartsRail.#reducedMotion.removeEventListener('change', this.#measure);
    removeEventListener('resize', this.#measure);
    removeEventListener('load', this.#measure);

    cancelAnimationFrame(this.#frame);
    this.#ticking = false;
  }

  /** Der Editor rendert Kacheln neu, ohne das Element auszutauschen. */
  updatedCallback() {
    super.updatedCallback();
    this.#cards = this.#queryCards();
    this.#measure();
  }

  #queryCards() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.part')));
  }

  get #enhanced() {
    return this.hasAttribute('data-enhanced');
  }

  /* ---------------------------------------------------------------------- */
  /* Scroll-Kopplung                                                          */
  /* ---------------------------------------------------------------------- */

  #bindScroll() {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#requestUpdate, { passive: true });
  }

  /** Beim Wechsel über 990 px wechselt der Scrollport. Erst abhängen, dann neu setzen. */
  #rebind = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#requestUpdate);
    this.#bindScroll();
    this.#measure();
  };

  #measure = () => {
    if (!this.#connected) return;
    const { track, stage, rail } = this.refs;
    if (!track || !stage || !rail) return;

    if (!(PartsRail.#desktop.matches && !PartsRail.#reducedMotion.matches)) {
      this.removeAttribute('data-enhanced');
      track.style.height = '';
      stage.style.height = '';
      stage.style.top = '';
      rail.style.transform = '';
      this.#weg = 0;
      this.#top = 0;
      this.#update();
      return;
    }

    this.setAttribute('data-enhanced', '');
    this.#weg = Math.max(0, rail.scrollWidth - innerWidth);

    // Inhalt plus ein Drittel der Restluft; passt der Inhalt nicht, bleibt es
    // beim Entwurf (Bühne = Bildschirm, Versatz 0).
    const content = rail.offsetHeight;
    const height = Math.min(innerHeight, content + (innerHeight - content) * PartsRail.AIR);
    this.#top = (innerHeight - height) / 2;
    stage.style.height = `${height}px`;
    stage.style.top = `${this.#top}px`;
    track.style.height = `${height + this.#weg + innerHeight * PartsRail.TAIL}px`;
    this.#update();
  };

  /**
   * Scrollen innerhalb der Section: die Spur (mobil) — oder die Bühne, wenn der
   * Browser sie beim Fokussieren selbst verschoben hat. Die Bühne hat
   * `overflow: hidden` und darf nie seitlich stehen, sonst läge die Spur
   * doppelt verschoben.
   *
   * @param {Event} event
   */
  #onInnerScroll = (event) => {
    const { stage } = this.refs;
    if (event.target === stage && stage.scrollLeft !== 0) stage.scrollLeft = 0;
    this.#requestUpdate();
  };

  #requestUpdate = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    this.#ticking = false;
    const { track, rail } = this.refs;
    if (!track || !rail) return;

    if (this.#enhanced) {
      // 1 px Scroll = 1 px Fahrt; danach 30 % Viewport Auslauf.
      const x = Math.min(this.#weg, Math.max(0, this.#travel()));
      rail.style.transform = `translate3d(${-x}px,0,0)`;
    }

    this.#setCount(this.#nearest());
  }

  /** Gefahrene Strecke: wie weit die Spur über den Klebepunkt der Bühne hinaus ist. */
  #travel() {
    const { track } = this.refs;
    return track ? this.#top - track.getBoundingClientRect().top : 0;
  }

  /** Aktive Kachel = die, deren Mitte der Viewport-Mitte am nächsten liegt. */
  #nearest() {
    const center = innerWidth / 2;
    let best = 0;
    let distance = Infinity;
    this.#cards.forEach((card, i) => {
      const r = card.getBoundingClientRect();
      const d = Math.abs(r.left + r.width / 2 - center);
      if (d < distance) {
        distance = d;
        best = i;
      }
    });
    return best;
  }

  /** An der Ergebnis-Kachel dreht der Pfeil nach unten und führt zum nächsten Modul. */
  #setCount(index) {
    const { arrow } = this.refs;
    if (!arrow) return;

    const atEnd = this.#enhanced
      ? this.#weg > 0 && this.#travel() >= this.#weg - 2
      : index >= this.#cards.length - 1;

    if (atEnd === this.#atEnd && arrow.classList.contains('is-down') === atEnd) return;
    this.#atEnd = atEnd;
    arrow.classList.toggle('is-down', atEnd);
    arrow.setAttribute('aria-label', (atEnd ? this.dataset.labelDown : this.dataset.labelNext) ?? '');
  }

  /* ---------------------------------------------------------------------- */
  /* Pfeil                                                                    */
  /* ---------------------------------------------------------------------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    const lensButton = target.closest?.('.part__lens-button');
    if (lensButton) {
      this.#toggleTouchLens(/** @type {HTMLElement} */ (lensButton.closest('[data-part-lens]')));
      return;
    }

    if (target.closest?.('.parts-rail__arrow')) this.#advance();
  };

  /** Eine Kachel weiter — mobil wischt die Spur, am Desktop scrollt die Seite um eine Kachelbreite. */
  #advance() {
    const { rail } = this.refs;
    if (this.#atEnd) {
      this.#scrollToNextSection();
      return;
    }

    const first = this.#cards[0];
    if (!first || !rail) return;
    const step = first.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0);

    if (this.#enhanced) {
      getScrollContainer().scrollBy({ top: step, behavior: 'smooth' });
    } else {
      rail.scrollBy({ left: step, behavior: 'smooth' });
    }
  }

  #scrollToNextSection() {
    let next = this.closest('.shopify-section')?.nextElementSibling;
    while (next && !(next instanceof HTMLElement && next.offsetHeight > 0)) next = next.nextElementSibling;
    if (!next) return;

    // Die Kopfzeile ist sticky und läge sonst über dem Anfang des nächsten Moduls.
    // Im Entwurf `.nav.offsetHeight`; Horizon misst sie selbst und legt sie am body ab.
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    scrollTo({ top: getScrollTop() + next.getBoundingClientRect().top - header, behavior: 'smooth' });
  }

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (!this.#enhanced) return;
    const { stage, track } = this.refs;
    const target = /** @type {Element} */ (event.target);
    if (stage) stage.scrollLeft = 0;

    // Nur bei Tastaturfokus. Ein Mausklick auf den Lupen-Knopf fokussiert ihn
    // in Chrome ebenfalls — die Seite darf dabei nicht wegspringen.
    if (!target.matches?.(':focus-visible')) return;
    const card = target.closest?.('.part, .parts-rail__intro');
    if (!card || !(card instanceof HTMLElement) || !track) return;

    // Der Pfeil im Titelblock steht am Anfang der Spur: Fahrt zurück auf null.
    const x = card.matches('.parts-rail__intro')
      ? 0
      : Math.min(this.#weg, Math.max(0, card.offsetLeft - (innerWidth - card.offsetWidth) / 2));
    const trackTop = getScrollTop() + track.getBoundingClientRect().top;
    scrollTo({ top: trackTop - this.#top + x, behavior: 'smooth' });
  };

  /* ---------------------------------------------------------------------- */
  /* Lupe (Kohle-Kachel)                                                      */
  /* ---------------------------------------------------------------------- */

  /**
   * Maus: Lupe folgt dem Zeiger. Touch: Lupen-Knopf → Lupe erscheint mittig,
   * mit dem Finger ziehen; Tipp daneben schließt. Das hochaufgelöste Bild lädt
   * erst beim ersten Einsatz.
   *
   * @param {HTMLElement} media
   * @param {number} cx
   * @param {number} cy
   */
  #place(media, cx, cy) {
    const image = media.querySelector('.part__image');
    const lens = /** @type {HTMLElement | null} */ (media.querySelector('.part__lens'));
    if (!image || !lens) return;

    if (!media.dataset.lensLoaded && media.dataset.zoomSrc) {
      lens.style.backgroundImage = `url("${media.dataset.zoomSrc.replace(/"/g, '%22')}")`;
      media.dataset.lensLoaded = 'true';
    }

    const r = PartsRail.#shownRect(image);
    const b = media.getBoundingClientRect();
    const half = lens.offsetWidth / 2;
    const inner = lens.clientWidth / 2;
    const zoom = PartsRail.ZOOM;

    lens.style.left = `${cx - b.left - half}px`;
    lens.style.top = `${cy - b.top - half}px`;
    lens.style.backgroundSize = `${r.width * zoom}px ${r.height * zoom}px`;
    lens.style.backgroundPosition = `${-((cx - r.left) * zoom - inner)}px ${-((cy - r.top) * zoom - inner)}px`;
  }

  /**
   * Der tatsächlich gezeigte Bildbereich. Das `<img>` ist ein 74-%-Kasten mit
   * `object-fit: contain` — am Desktop nicht quadratisch (Bildhöhe hängt an
   * `svh`), das Bild steht darin zentriert mit Rand. Mit dem Kasten gerechnet
   * wäre das Zoom-Bild gestreckt und die Lupe versetzt. Ohne natürliche Maße
   * (Platzhalter-SVG, Bild noch nicht geladen) bleibt es beim Kasten.
   *
   * @param {Element} image
   * @returns {{ left: number, top: number, width: number, height: number }}
   */
  static #shownRect(image) {
    const box = image.getBoundingClientRect();
    const nw = image instanceof HTMLImageElement ? image.naturalWidth : 0;
    const nh = image instanceof HTMLImageElement ? image.naturalHeight : 0;
    if (!nw || !nh) return box;

    const s = Math.min(box.width / nw, box.height / nh);
    const width = nw * s;
    const height = nh * s;
    return {
      left: box.left + (box.width - width) / 2,
      top: box.top + (box.height - height) / 2,
      width,
      height,
    };
  }

  /**
   * @param {HTMLElement} media
   * @param {boolean} on
   * @param {boolean} [touch]
   */
  #setLens(media, on, touch = false) {
    media.classList.toggle('is-lens-on', on);
    media.classList.toggle('is-lens-touch', on && touch);
    media.querySelector('.part__lens-button')?.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  /** @param {HTMLElement | null} media */
  #toggleTouchLens(media) {
    if (!media) return;
    if (media.classList.contains('is-lens-touch')) {
      this.#setLens(media, false);
      return;
    }
    this.#setLens(media, true, true);
    const image = media.querySelector('.part__image');
    if (!image) return;
    const r = PartsRail.#shownRect(image);
    this.#place(media, r.left + r.width / 2, r.top + r.height / 2);
  }

  /** @param {Event} event */
  #lensFrom(event) {
    const target = /** @type {Element | null} */ (event.target);
    return /** @type {HTMLElement | null} */ (target?.closest?.('[data-part-lens]') ?? null);
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const media = this.#lensFrom(event);
    if (!media) return;

    if (event.pointerType === 'mouse') {
      if (!media.classList.contains('is-lens-on')) this.#setLens(media, true);
      this.#place(media, event.clientX, event.clientY);
    } else if (media.classList.contains('is-lens-touch')) {
      this.#place(media, event.clientX, event.clientY);
    }
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const media = this.#lensFrom(event);
    if (!media || event.pointerType === 'mouse' || !media.classList.contains('is-lens-touch')) return;
    if (/** @type {Element} */ (event.target).closest('.part__lens-button')) return;
    this.#place(media, event.clientX, event.clientY);
  };

  /**
   * Ersatz für `pointerleave`, das nicht bubbelt: Die Maus hat die Kachel
   * verlassen, wenn das neue Ziel nicht mehr darin liegt.
   *
   * @param {PointerEvent} event
   */
  #onPointerOut = (event) => {
    if (event.pointerType !== 'mouse') return;
    const media = this.#lensFrom(event);
    if (!media) return;
    const to = /** @type {Node | null} */ (event.relatedTarget);
    if (to && media.contains(to)) return;
    this.#setLens(media, false);
  };

  /** Tipp daneben schließt die Touch-Lupe. @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    for (const media of this.querySelectorAll('[data-part-lens].is-lens-touch')) {
      if (!media.contains(/** @type {Node} */ (event.target))) this.#setLens(/** @type {HTMLElement} */ (media), false);
    }
  };
}

if (!customElements.get('parts-rail')) {
  customElements.define('parts-rail', PartsRail);
}
